
import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierFull, UnarchiveFilled, UnarchiveLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { StateCode, StateCodeToCreate, StateCodeToUpdate } from '@services/member/list/state-codes/state-codes.types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useStateCodesPageVM } from './useStateCodesPageVM';
import { ModifierLine } from '@icons';
import { Formik } from 'formik';

export default function StateCodesPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveStateCodes,
        needReload,
        setIsArchived,
        updateStateCode,
        createStateCode,
        MySwal
    } = useStateCodesPageVM();
    const columnHelper = createColumnHelper<StateCode>();
    const navigate = useNavigate();

    const handleModificationStateCodes = async (stateCode: StateCodeToCreate | null, id?: number) => {
        await MySwal.fire<StateCodeToUpdate>({
            title: stateCode ? "Modification d'un code état" : "Création d'un code état",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: stateCode ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ code: stateCode?.code ?? '', label: stateCode?.label ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updateStateCode({
                                id: id,
                                code: values.code,
                                label: values.label
                            });
                        } else {
                            createStateCode({
                                code: values.code,
                                label: values.label
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-state-code" onSubmit={handleSubmit}>
                            <Input name="code" required label="Code" placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" required label="Libellé" placeholder="Libellé" value={values.label} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-state-code') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationStateCodes(
                                    { code: info.row.original.code, label: info.row.original.label },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveStateCodes([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<StateCode>[];

    return (
        <>
            <div className="settings-tabs-content state-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/state-codes/${row.id}`)}
                    defaultSorting={{
                        field: 'code',
                        direction: 'desc'
                    }}
                    onMetadata={handleMetadata}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationStateCodes(null)}
                    createButtonLabel="Créer un code"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'state_code',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: StateCode[]) => handleArchiveStateCodes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
